'use client';

import { useEffect, useRef } from 'react';
import { sendEventToGTM } from '../../helpers/gtm.helper';

interface AutoTrackerProps {
    children: React.ReactNode;
    selector?: string; 
}

export default function SectionAutoTracker({ children, selector = '.track-section' }: AutoTrackerProps) {
    const containerRef = useRef<HTMLDivElement>(null);
    // Track which elements are ALREADY being observed so we don't double-attach
    const observedElements = useRef<Set<Element>>(new Set());
    // Track which elements have ALREADY been sent to GTM so we never send twice
    const sentElements = useRef<Set<Element>>(new Set());

    useEffect(() => {
        if (!containerRef?.current || typeof window === 'undefined') return;

        console.log("SectionAutoTracker entries ---- ", containerRef?.current);
        // 1. Setup the Scroll Observer (Intersection)
        const intersectionObserver = new IntersectionObserver(
            (entries) => {
                console.log("SectionAutoTracker entries ---- ", entries);
                entries?.forEach((entry) => {
                    if (entry?.isIntersecting && !sentElements?.current?.has(entry?.target)) {
                        const element = entry.target as HTMLElement;
                        
                        // Mark as sent immediately to prevent race conditions
                        sentElements?.current?.add(element);

                        const sectionName =
                            element?.id ||
                            element?.getAttribute('data-section-id') ||
                            element?.getAttribute('data-section-name') || 
                            element?.getAttribute('aria-label') || 
                            `section_${Array.from(containerRef?.current!.querySelectorAll(selector))?.indexOf(element)}`;

                        sendEventToGTM({
                            event: "section_view",
                            pagePath: window?.location?.pathname,
                            location: sectionName,
                        });

                        // Stop watching this specific element
                        intersectionObserver?.unobserve(element);
                    }
                });
            },
            { threshold: 0.15 } // Lower threshold (15%) to ensure it triggers on mobile
        );

        // 2. The "Scanner" function
        const scanAndObserve = () => {
            if (!containerRef?.current) return;
            const sections = containerRef?.current?.querySelectorAll(selector);
            
            sections?.forEach((section) => {
                if (!observedElements?.current?.has(section)) {
                    intersectionObserver?.observe(section);
                    observedElements?.current?.add(section);
                }
            });
        };

        // 3. Setup the Mutation Observer (The "Waiter")
        // We use 'childList', 'subtree', and 'attributes' to be safe
        const mutationObserver = new MutationObserver((mutations) => {
            scanAndObserve();
        });

        mutationObserver.observe(containerRef.current, {
            childList: true,
            subtree: true,
            attributes: false // Set to true if sections change classes dynamically
        });

        // Initial scan for static content
        scanAndObserve();

        return () => {
            mutationObserver?.disconnect();
            intersectionObserver?.disconnect();
            observedElements?.current?.clear();
            sentElements?.current?.clear();
        };
    }, [selector, children]); // Re-run if selector or high-level children change

    return <div ref={containerRef} className="gtm-auto-tracker">{children}</div>;
}